// The claims behind one block (or the paid list, or the approval queue): the header
// with the block's dollars, claim count, total outstanding, owner and action; the
// buttons (Assign to <owner>, Add to Worklist, Export CSV, Mark block resolved); and
// the table. The same component opens from a chart bar and a table row's "open" button.
//
// Cleaned up (David, 2026-10-25): ten columns, not thirteen. Paid is insurance +
// patient and Outstanding is the total, each with the split on hover; the CSV export
// keeps every column. The explanations that sat under the table are tooltips now.
//
// The CPT cell shows the AFFECTED lines first (the line with the denial, the unpriced
// line, the line with money still open), each marked with ✱ when the payer did not
// price it, and folds the rest of the visit under "other lines on this visit" (David's
// Soldano review, 2026-10-14: a visit processed several times listed every CPT of every
// processing). The reason cell carries only the reason codes (with their meanings), the
// "no ERA" sentence and the biller's worklist reason; the ERA's own sequence remark
// ("last status: processed as primary") lives in the cell's tooltip with the dated
// history, not in the column. The Status cell says what the source system said when
// its word differs from Atlas's ("draft · source: HOLD"). The Expected and Allowed
// cells carry the rule that produced them as a tooltip. Names are shown here because
// this is inside the app; the server logs the read.

import React, { useEffect, useRef, useState } from "react";
import ClaimListActions from "../claimLists/ClaimListActions";

import { cents, count, money } from "../ledgerReview/execCells";
import {
  displayStatusKey,
  statusColor,
  SELF_PAY_CLOSED,
  SELF_PAY_CLOSED_LABEL,
} from "../../utils/claimStatus";

// The server's own column keys (the CSV's), every one of them; the modal shows
// MODAL_COLUMNS.
export const COLUMNS = [
  ["patient", "Patient"],
  ["dos", "DOS"],
  ["cpt", "CPT"],
  ["reason_codes", "Reason codes (ERA)"],
  ["billed", "Billed"],
  ["expected", "Expected"],
  ["allowed", "Allowed"],
  ["insurance_paid", "Insurance paid"],
  ["patient_paid", "Patient paid"],
  ["insurance_outstanding", "Insurance outstanding"],
  ["patient_outstanding", "Patient outstanding"],
  ["total_outstanding", "Total outstanding"],
  ["status", "Status"],
];

export const MODAL_COLUMNS = [
  ["patient", "Patient"],
  ["dos", "DOS"],
  ["cpt", "CPT"],
  ["reason_codes", "Reason"],
  ["billed", "Billed"],
  ["expected", "Expected"],
  ["allowed", "Allowed"],
  ["paid", "Paid"],
  ["total_outstanding", "Outstanding"],
  ["status", "Status"],
];

const NUMERIC = new Set([
  "billed",
  "expected",
  "allowed",
  "paid",
  "total_outstanding",
]);

export const BUNDLED_TITLE =
  "line the payer bundled into the visit (CO-45 for its whole charge beside a " +
  "payment on the claim): worth nothing on its own, not in flight";

export const UNPRICED_TITLE =
  "line not priced by the payer (denied or not adjudicated): left out of Allowed, " +
  "worth its usual allowed (or its charge when none exists), all of it open";

export const DUPLICATE_TITLE =
  "duplicate charge line: an unpriced copy of an adjudicated line of this claim " +
  "(the same CPT and charge, nothing on it); worth nothing, not in flight";

export const COVERED_TITLE =
  "line nothing came back on, covered by the claim's allowed (the ceiling for the " +
  "whole claim): worth only what the priced lines leave of it, normally nothing";

// Where a line's reason codes came from (ledger_review_service.CODE_SOURCES) and, for
// a line without any, why (CODE_REASONS): the chip after the CPT, the hover's words.
export const CODE_SOURCE_LABELS = {
  835: "codes from a remittance Atlas holds (835)",
  pm: "codes carried from the PM's payment report or feed (PracticeSuite, Practice Fusion, Cobalt)",
  manual: "codes keyed from a paper or portal EOB",
  none: "no reason codes held for this line",
};
export const CODE_REASON_LABELS = {
  in_flight: "no payer answer yet (expected)",
  pm_payment_no_era: "cash posted from the PM, no 835 matched to the claim",
  era_unmatched:
    "an 835 exists for the claim but none of its rows matched this line",
  era_no_codes:
    "an 835 row matched the line with a payment and no adjustment code",
  other: "no remittance, no PM codes, no manual EOB",
};

// Where a line's reason codes came from, or why there are none: hover text on the CPT.
export function codeSourceText(l) {
  if (!l.code_source) return "";
  if (l.code_source !== "none") {
    return CODE_SOURCE_LABELS[l.code_source] || l.code_source;
  }
  const reason = l.code_reason || "other";
  return `${CODE_SOURCE_LABELS.none}: ${CODE_REASON_LABELS[reason] || reason}`;
}

// Home health (2026-10-17): the period's visits against its LUPA threshold, when the
// threshold is known (the practice's loaded table, else the CMS case-mix table). A
// LUPA period was paid per visit by design: its chip says so, and it is never
// "underpaid".
export function lupaText(lupa) {
  if (!lupa || lupa.threshold === null || lupa.threshold === undefined)
    return null;
  const visits =
    lupa.visits === null || lupa.visits === undefined ? "?" : lupa.visits;
  return lupa.lupa
    ? `LUPA: ${visits} of ${lupa.threshold} visits`
    : `${visits} visits (LUPA below ${lupa.threshold})`;
}

function lupaChip(row) {
  const text = lupaText(row.lupa);
  if (!text) return null;
  const source =
    row.lupa.threshold_source === "cms_table"
      ? "the CMS case-mix table"
      : `the practice's loaded thresholds (${(row.lupa.threshold_source || "").replace("loaded ", "").replace(/[()]/g, "")})`;
  return (
    <>
      {" "}
      <span
        className={`wf-chip ${row.lupa.lupa ? "" : "ok"}`}
        title={
          row.lupa.lupa
            ? `A LUPA period: fewer visits than the HIPPS threshold from ${source}, so the payer pays per visit. Expected, not an underpayment.`
            : `A full period: at or above the LUPA threshold from ${source}.`
        }
        data-testid="waterfall-lupa"
      >
        {text}
      </span>
    </>
  );
}

// An unpriced line worth nothing on its own: a muted word, never the red ✱.
const MUTED_MARKS = {
  bundled: {
    word: "bundled",
    title: BUNDLED_TITLE,
    testId: "waterfall-bundled",
  },
  duplicate: {
    word: "duplicate",
    title: DUPLICATE_TITLE,
    testId: "waterfall-duplicate",
  },
  covered: {
    word: "covered",
    title: COVERED_TITLE,
    testId: "waterfall-covered",
  },
};

// "11721: CO-96 non-covered $55.00 ×4" per line; the dated history is the tooltip.
export function reasonText(group) {
  return group.codes
    .map((c) => {
      const meaning = c.meaning ? ` ${c.meaning}` : "";
      const times = c.count > 1 ? ` ×${c.count}` : "";
      return `${c.label}${meaning} ${cents(c.amount)}${times}`;
    })
    .join("; ");
}

function cptCodes(lines, testId) {
  return lines.map((l, i) => (
    <span
      key={`${l.code}-${i}`}
      className="wf-cpt"
      title={
        [l.why ? `affected: ${l.why}` : "", codeSourceText(l)]
          .filter(Boolean)
          .join(" · ") || undefined
      }
      data-testid={testId}
    >
      {i > 0 ? " " : ""}
      {l.code}
      {l.priced ? null : MUTED_MARKS[l.price_basis] ? (
        <span
          className="wf-bundled"
          title={MUTED_MARKS[l.price_basis].title}
          data-testid={MUTED_MARKS[l.price_basis].testId}
        >
          {MUTED_MARKS[l.price_basis].word}
        </span>
      ) : (
        <span
          className="wf-unpriced"
          title={UNPRICED_TITLE}
          data-testid="waterfall-unpriced"
        >
          ✱
        </span>
      )}
    </span>
  ));
}

// The lines the block is about first; the rest of the visit folded away. A row with
// no affected line (a claim paid clean) or with nothing else shows every line as is.
export function splitLines(lines) {
  const affected = lines.filter((l) => l.affected);
  if (!affected.length || affected.length === lines.length) {
    return { first: lines, other: [] };
  }
  return { first: affected, other: lines.filter((l) => !l.affected) };
}

function CptCell({ row }) {
  const { first, other } = splitLines(row.cpt || []);
  return (
    <span className="wf-mono">
      {cptCodes(first, "waterfall-cpt")}
      {other.length ? (
        <details className="wf-other-lines" data-testid="waterfall-other-lines">
          <summary>other lines on this visit ({other.length})</summary>
          {cptCodes(other, "waterfall-cpt-other")}
        </details>
      ) : null}
    </span>
  );
}

function ReasonCodes({ row }) {
  const groups = row.reason_codes || [];
  // The dated history, and the ERA's own sequence remark, are detail: the tooltip.
  const history = (row.reason_history || [])
    .concat(row.last_status ? [`last ERA status: ${row.last_status}`] : [])
    .join("\n");
  const biller = row.worklist_reason ? (
    <>
      <br />
      <span
        className="wf-biller-reason"
        title={`Source: ${row.worklist_reason_source || "the biller's working sheet"}`}
        data-testid="waterfall-worklist-reason"
      >
        biller: {row.worklist_reason}
      </span>
    </>
  ) : null;
  if (!groups.length) {
    // The server's note can carry a parenthetical explanation; the cell shows the
    // short part and the whole sentence is the hover (David, 2026-10-25).
    const note = row.reason_note || "";
    const short = note.replace(/\s*\(.*\)\s*$/, "");
    const hover = [short !== note ? note : "", history]
      .filter(Boolean)
      .join("\n");
    return (
      <span
        className="wf-muted"
        title={hover || undefined}
        data-testid="waterfall-reason-codes"
      >
        {short || "—"}
        {biller}
      </span>
    );
  }
  return (
    <span
      className="wf-codes"
      title={history || undefined}
      data-testid="waterfall-reason-codes"
    >
      {groups.map((g, i) => (
        <span key={`${g.line || "claim"}-${i}`} className="wf-code-line">
          {i > 0 ? <br /> : null}
          <span className="wf-mono">{g.line || "claim"}:</span> {reasonText(g)}
        </span>
      ))}
      {biller}
    </span>
  );
}

// The claim's status exactly as the Claims tab shows it, nothing added (David,
// 2026-10-25): the stored claim status, grouped and coloured by utils/claimStatus.
// A self-pay claim the self-pay rule closed reads "Self-pay, closed" (the review's
// status label), never the PM's "patient".
function StatusCell({ row }) {
  const closed = row.status === SELF_PAY_CLOSED_LABEL;
  const key = closed
    ? SELF_PAY_CLOSED
    : displayStatusKey(row.ledger_status || row.status);
  return (
    <span
      className="wf-status-badge"
      style={{ background: statusColor(key) }}
      data-testid="waterfall-status-label"
    >
      {closed ? SELF_PAY_CLOSED_LABEL : key}
    </span>
  );
}

function cell(key, row) {
  if (key === "patient") {
    return (
      <>
        {row.patient || (
          <span className="wf-muted">(no name on the claim)</span>
        )}{" "}
        <span className="wf-mono wf-muted">#{row.id}</span>
      </>
    );
  }
  if (key === "cpt") return <CptCell row={row} />;
  if (key === "reason_codes") return <ReasonCodes row={row} />;
  if (key === "dos" && row.period_from && row.period_through) {
    // A home-health period: the bill date, with the 30-day period it covers (the
    // through date is what the A/R ages from).
    return (
      <>
        {row.dos}
        <br />
        <span
          className="wf-muted"
          title="The 30-day period the claim bills; open money ages from its through date"
          data-testid="waterfall-period"
        >
          period {row.period_from} – {row.period_through}
        </span>
      </>
    );
  }
  if (key === "expected") {
    return (
      <span title={row.expected_note || undefined}>{cents(row.expected)}</span>
    );
  }
  if (key === "allowed") {
    return (
      <>
        <span title={row.allowed_note || undefined}>
          {row.allowed === null || row.allowed === undefined
            ? "—"
            : cents(row.allowed)}
        </span>
        {row.allowed_corrected ? (
          <>
            {" "}
            <span
              className="wf-chip warn"
              title={
                row.allowed_correction || "the stated allowed was corrected"
              }
              data-testid="waterfall-allowed-corrected"
            >
              corrected
            </span>
          </>
        ) : null}
        {row.priced_at_charge ? (
          <>
            {" "}
            <span
              className="wf-chip bad"
              title="No usual allowed for the payer or the CPT: a line is priced at its billed charge"
              data-testid="waterfall-priced-at-charge"
            >
              at charge
            </span>
          </>
        ) : null}
        {row.visit_capped ? (
          <>
            {" "}
            <span
              className="wf-chip"
              title={row.visit_note || "priced per visit"}
              data-testid="waterfall-visit-capped"
            >
              per visit
            </span>
          </>
        ) : null}
        {lupaChip(row)}
        {Number(row.below_usual || 0) > 0 ? (
          <>
            {" "}
            <span
              className="wf-chip"
              title="What the usual allowed for the payer and CPT exceeds the stated allowed by: beyond the price, an appeal's money"
              data-testid="waterfall-below-usual"
            >
              {cents(row.below_usual)} below usual
            </span>
          </>
        ) : null}
      </>
    );
  }
  if (key === "status") return <StatusCell row={row} />;
  if (key === "paid") {
    const paid =
      Number(row.insurance_paid || 0) + Number(row.patient_paid || 0);
    return (
      <span
        title={`Insurance ${cents(row.insurance_paid || 0)} · patient ${cents(row.patient_paid || 0)}`}
        data-testid="waterfall-paid"
      >
        {cents(paid)}
      </span>
    );
  }
  if (key === "total_outstanding") {
    return (
      <strong
        title={`Insurance ${cents(row.insurance_outstanding || 0)} · patient ${cents(row.patient_outstanding || 0)}`}
        data-testid="waterfall-outstanding"
      >
        {cents(row[key])}
      </strong>
    );
  }
  if (NUMERIC.has(key)) return cents(row[key]);
  return row[key];
}

// A bucket key as a claim list's source (waterfall:<bucket key>). The server accepts
// letters, digits, "_", ".", ":" and "-", at most 100; a custom block's key is folded to that.
export function listSource(block) {
  const key = String((block && block.key) || "block")
    .replace(/[^A-Za-z0-9_.:-]/g, "_")
    .slice(0, 100);
  return `waterfall:${key || "block"}`;
}

export default function BlockModal({
  practiceId,
  onOpenList,
  block,
  kind = "block",
  rangeLabel,
  readOnly = false,
  isAdmin = false,
  busy = false,
  onClose,
  onAssign,
  onWorklist,
  onExport,
  onResolve,
  onDecide,
}) {
  const closeRef = useRef(null);
  // Claims ticked for a claim list. Starts empty for each block opened.
  const [listIds, setListIds] = useState(() => new Set());
  useEffect(() => {
    setListIds(new Set());
  }, [block && block.key]);
  useEffect(() => {
    if (closeRef.current) closeRef.current.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!block) return null;
  const rows = block.rows || [];
  const totalOut = rows.reduce(
    (a, r) => a + Number(r.total_outstanding || 0),
    0,
  );
  const isQueue = kind === "approval";
  const isPaid = kind === "paid";
  const pending = isQueue ? rows.filter((r) => !r.decision) : [];
  const allListed = rows.length > 0 && rows.every((r) => listIds.has(r.id));
  const toggleListed = (id) =>
    setListIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAllListed = () =>
    setListIds(allListed ? new Set() : new Set(rows.map((r) => r.id)));

  return (
    <div
      className="modal-overlay wf-modal-bg"
      data-testid="waterfall-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="wf-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wf-modal-title"
      >
        <header className="wf-modal-header">
          <div>
            <h2 id="wf-modal-title">{block.name}</h2>
            <p>
              {money(block.dollars)} · {count(block.claims)} claims
              {!isPaid ? ` · ${money(totalOut)} outstanding` : ""}
              {rangeLabel ? ` · ${rangeLabel}` : ""}
            </p>
          </div>
          <button
            type="button"
            className="wf-close"
            onClick={onClose}
            ref={closeRef}
          >
            Close
          </button>
        </header>
        <div className="wf-modal-body">
          <div className="wf-toolbar">
            {!readOnly && !isPaid && !isQueue && block.owner ? (
              <button
                type="button"
                className="wf-btn"
                disabled={busy || !rows.length}
                onClick={() => onAssign(block)}
              >
                Assign to {block.owner}
              </button>
            ) : null}
            {!readOnly && !isPaid && !isQueue ? (
              <button
                type="button"
                className="wf-btn"
                disabled={busy || !rows.length}
                onClick={() => onWorklist(block)}
              >
                Add to Worklist
              </button>
            ) : null}
            <button
              type="button"
              className="wf-btn"
              disabled={busy}
              onClick={() => onExport(block)}
              data-testid="waterfall-export-block"
            >
              Export CSV
            </button>
            {!readOnly && !isPaid && !isQueue ? (
              <button
                type="button"
                className="wf-btn"
                disabled={busy || !block.id}
                title={
                  block.id
                    ? ""
                    : block.builtin
                      ? "A built-in block is not stored, so it cannot be marked resolved"
                      : "Save the practice's blocks first (an admin seeds them) to mark one resolved"
                }
                onClick={() => onResolve(block, !block.resolved)}
              >
                {block.resolved ? "Reopen block" : "Mark block resolved"}
              </button>
            ) : null}
            {isQueue && isAdmin && !readOnly ? (
              <>
                <button
                  type="button"
                  className="wf-btn primary"
                  disabled={busy || !pending.length}
                  onClick={() =>
                    onDecide(
                      pending.map((r) => r.id),
                      "approved",
                    )
                  }
                >
                  Approve all pending ({pending.length})
                </button>
                <button
                  type="button"
                  className="wf-btn"
                  disabled={busy || !pending.length}
                  onClick={() =>
                    onDecide(
                      pending.map((r) => r.id),
                      "rejected",
                    )
                  }
                >
                  Reject all pending
                </button>
              </>
            ) : null}
            {practiceId && rows.length ? (
              <ClaimListActions
                practiceId={practiceId}
                selectedIds={[...listIds]}
                source={listSource(block)}
                defaultTitle={block.name || ""}
                onDone={() => setListIds(new Set())}
                onOpenList={onOpenList}
              />
            ) : null}
            {block.resolved ? (
              <span className="wf-chip ok">
                resolved by {block.resolved_by || "—"}{" "}
                {block.resolved_at ? block.resolved_at.slice(0, 10) : ""}
              </span>
            ) : null}
          </div>
          {rows.length ? (
            <div className="wf-tbl">
              <table data-testid="waterfall-modal-table">
                <thead>
                  <tr>
                    {practiceId ? (
                      <th className="wf-list-col">
                        <input
                          type="checkbox"
                          aria-label="Select every claim in this block for a list"
                          checked={allListed}
                          onChange={toggleAllListed}
                        />
                      </th>
                    ) : null}
                    {MODAL_COLUMNS.map(([key, label]) => (
                      <th key={key} className={NUMERIC.has(key) ? "n" : ""}>
                        {label}
                      </th>
                    ))}
                    {isQueue ? <th className="n">Written off</th> : null}
                    {isQueue ? <th>Decision</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid="waterfall-modal-row">
                      {practiceId ? (
                        <td className="wf-list-col">
                          <input
                            type="checkbox"
                            aria-label="Select for a list"
                            checked={listIds.has(r.id)}
                            onChange={() => toggleListed(r.id)}
                          />
                        </td>
                      ) : null}
                      {MODAL_COLUMNS.map(([key]) => (
                        <td key={key} className={NUMERIC.has(key) ? "n" : ""}>
                          {cell(key, r)}
                        </td>
                      ))}
                      {isQueue ? (
                        <td className="n">{cents(r.written_off)}</td>
                      ) : null}
                      {isQueue ? (
                        <td className="wf-decision">
                          {r.decision ? (
                            <span
                              className={`wf-chip ${r.decision === "approved" ? "ok" : "bad"}`}
                            >
                              {r.decision}
                              {r.decided_by ? ` · ${r.decided_by}` : ""}
                            </span>
                          ) : (
                            <span className="wf-chip warn">pending</span>
                          )}
                          {isAdmin && !readOnly ? (
                            <span className="wf-decision-actions">
                              {r.decision !== "approved" ? (
                                <button
                                  type="button"
                                  className="wf-btn sm"
                                  disabled={busy}
                                  onClick={() => onDecide([r.id], "approved")}
                                >
                                  Approve
                                </button>
                              ) : null}
                              {r.decision !== "rejected" ? (
                                <button
                                  type="button"
                                  className="wf-btn sm"
                                  disabled={busy}
                                  onClick={() => onDecide([r.id], "rejected")}
                                >
                                  Reject
                                </button>
                              ) : null}
                              {r.decision ? (
                                <button
                                  type="button"
                                  className="wf-btn sm"
                                  disabled={busy}
                                  onClick={() => onDecide([r.id], null)}
                                >
                                  Clear
                                </button>
                              ) : null}
                            </span>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="wf-note">No claims in this block for the range.</p>
          )}
        </div>
      </div>
    </div>
  );
}
